import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tab,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Tabs,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Badge,
  Tooltip,
  Alert,
  CircularProgress,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  FormControlLabel,
  Switch,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import { 
  Add, 
  Delete, 
  Edit, 
  Person,
  Email,
  Phone,
  LocationOn,
  Business,
  Star,
  StarBorder,
  TrendingUp,
  TrendingDown,
  Visibility,
  Print,
  Download,
  FilterList,
  Refresh,
  Assessment,
  ShoppingCart,
  Payment,
  LocalShipping,
  Notifications,
  ExpandMore,
  Search,
  FilterAlt,
  Sort,
  MoreVert,
  ContactSupport,
  History,
  Analytics,
  Group,
  PersonAdd,
  BusinessCenter,
  AttachMoney,
  Schedule,
  CheckCircle,
  Warning,
  Error,
  Info,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { useRbac } from '../../context/RbacContext';
import { 
  useGetCustomersQuery, 
  useGetTopCustomersQuery, 
  useCreateCustomerMutation, 
  useUpdateCustomerMutation, 
  useDeleteCustomerMutation 
} from '../../api/customerApi';
import { Customer, CreateCustomer, UpdateCustomer } from '../../types/customer';

interface CustomerManagementProps {
  onCustomerSelected?: (customer: Customer) => void;
}

interface CustomerAnalytics {
  totalCustomers: number;
  activeCustomers: number;
  newCustomersThisMonth: number;
  averageOrderValue: number;
  topSpendingCustomer: Customer | null;
  customerSatisfactionScore: number;
}

interface CustomerSegment {
  id: string;
  name: string;
  criteria: string;
  customerCount: number;
  averageValue: number;
}

export const CustomerManagement: React.FC<CustomerManagementProps> = (props) => {
  const { canManage } = useRbac();
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [selectedTab, setSelectedTab] = useState(0);
  const { data: customers, isLoading: isCustomersLoading, isError: isCustomersError, refetch } = useGetCustomersQuery();
  const { data: topCustomers, isLoading: isTopCustomersLoading } = useGetTopCustomersQuery();
  const [newCustomer, setNewCustomer] = useState<Partial<Customer>>({
    name: '',
    email: '',
    phone: '',
    address: '',
    status: 'active',
    totalOrders: 0,
    totalSpent: 0,
    lastOrderDate: new Date().toISOString(),
    company: '',
    notes: '',
    tags: [],
  });
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState({
    status: 'all',
    source: 'all',
    priority: 'all',
    assignedTo: 'all'
  });
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [createCustomer] = useCreateCustomerMutation();
  const [updateCustomer] = useUpdateCustomerMutation();
  const [deleteCustomer] = useDeleteCustomerMutation();

  // Mock analytics data
  const [analytics, setAnalytics] = useState<CustomerAnalytics>({
    totalCustomers: 0,
    activeCustomers: 0,
    newCustomersThisMonth: 0,
    averageOrderValue: 0,
    topSpendingCustomer: null,
    customerSatisfactionScore: 0
  });

  const [segments, setSegments] = useState<CustomerSegment[]>([
    {
      id: '1',
      name: 'VIP Customers',
      criteria: 'Total spent > $10,000',
      customerCount: 15,
      averageValue: 15000
    },
    {
      id: '2',
      name: 'Regular Customers',
      criteria: 'Total spent $1,000 - $10,000',
      customerCount: 45,
      averageValue: 3500
    },
    {
      id: '3',
      name: 'New Customers',
      criteria: 'Joined in last 30 days',
      customerCount: 12,
      averageValue: 800
    }
  ]);

  useEffect(() => {
    if (customers) {
      setAnalytics({
        totalCustomers: customers.length,
        activeCustomers: customers.filter(c => c.status === 'active').length,
        newCustomersThisMonth: customers.filter(c => {
          if (!c.lastOrderDate) return false;
          const customerDate = parseISO(c.lastOrderDate);
          const monthAgo = new Date();
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          return customerDate > monthAgo;
        }).length,
        averageOrderValue: customers.reduce((sum, c) => sum + (c.totalSpent || 0), 0) / customers.length,
        topSpendingCustomer: customers.reduce((max, c) => (c.totalSpent || 0) > (max.totalSpent || 0) ? c : max, customers[0]),
        customerSatisfactionScore: 4.2
      });
    }
  }, [customers]);

  const handleCreateCustomer = async () => {
    if (!canManage('manager:customers') && !canManage('admin:customers')) return;

    const customerData: CreateCustomer = {
      name: newCustomer.name || '',
      email: newCustomer.email || '',
      phone: newCustomer.phone || '',
      address: newCustomer.address || '',
      status: newCustomer.status || 'active',
      company: newCustomer.company || '',
      notes: newCustomer.notes || '',
      tags: newCustomer.tags || [],
    };

    if (!customerData.name || !customerData.email || !customerData.phone) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await createCustomer(customerData);
      setNewCustomer({
        name: '',
        email: '',
        phone: '',
        address: '',
        status: 'active',
        totalOrders: 0,
        totalSpent: 0,
        lastOrderDate: new Date().toISOString(),
        company: '',
        notes: '',
        tags: [],
      });
      setOpenDialog(false);
      refetch();
    } catch (err) {
      setError('Failed to create customer');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCustomer = async () => {
    if (!selectedCustomer || (!canManage('manager:customers') && !canManage('admin:customers'))) return;

    const updatedCustomer: UpdateCustomer = {
      name: selectedCustomer.name,
      email: selectedCustomer.email,
      phone: selectedCustomer.phone,
      address: selectedCustomer.address,
      status: selectedCustomer.status,
      totalOrders: selectedCustomer.totalOrders,
      totalSpent: selectedCustomer.totalSpent,
      lastOrderDate: selectedCustomer.lastOrderDate,
      company: selectedCustomer.company,
      notes: selectedCustomer.notes,
      tags: selectedCustomer.tags,
    };

    if (!updatedCustomer.name || !updatedCustomer.email || !updatedCustomer.phone) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await updateCustomer({ id: selectedCustomer.id, data: updatedCustomer });
      setSelectedCustomer(null);
      setOpenDialog(false);
      refetch();
    } catch (err) {
      setError('Failed to update customer');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCustomer = async (id: string) => {
    if (!canManage('manager:customers') && !canManage('admin:customers')) return;

    if (!window.confirm('Are you sure you want to delete this customer?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await deleteCustomer(id);
      refetch();
    } catch (err) {
      setError('Failed to delete customer');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCustomer = (customer: Customer) => {
    if (props.onCustomerSelected) {
      props.onCustomerSelected(customer);
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const filteredCustomers = customers?.filter(customer => {
    const matchesSearch = customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer.company?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filter.status === 'all' || customer.status === filter.status;
    const matchesSource = filter.source === 'all' || customer.source === filter.source;
    const matchesPriority = filter.priority === 'all' || customer.priority === filter.priority;
    
    return matchesSearch && matchesStatus && matchesSource && matchesPriority;
  }) || [];

  const sortedCustomers = [...filteredCustomers].sort((a, b) => {
    let aValue = a[sortBy as keyof Customer];
    let bValue = b[sortBy as keyof Customer];
    
    if (typeof aValue === 'string') aValue = aValue.toLowerCase();
    if (typeof bValue === 'string') bValue = bValue.toLowerCase();
    
    if ((aValue as any) < (bValue as any)) return sortOrder === 'asc' ? -1 : 1;
    if ((aValue as any) > (bValue as any)) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const renderStatusChip = (status: string) => {
    const color = status === 'active' ? 'success' : status === 'inactive' ? 'error' : 'warning';
    return <Chip label={status} color={color} size="small" />;
  };

  const renderPriorityChip = (priority: string) => {
    const color = priority === 'high' ? 'error' : priority === 'medium' ? 'warning' : 'success';
    return <Chip label={priority} color={color} size="small" />;
  };

  const renderSourceChip = (source: string) => {
    const color = source === 'direct' ? 'primary' : source === 'referral' ? 'secondary' : 'default';
    return <Chip label={source} color={color} size="small" />;
  };

  if (isCustomersLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isCustomersError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error loading customers
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Group color="primary" />
        Customer Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Analytics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Customers
              </Typography>
              <Typography variant="h4">
                {analytics.totalCustomers}
              </Typography>
              <LinearProgress variant="determinate" value={100} sx={{ mt: 1 }} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Customers
              </Typography>
              <Typography variant="h4" color="success.main">
                {analytics.activeCustomers}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={analytics.totalCustomers ? (analytics.activeCustomers / analytics.totalCustomers) * 100 : 0} 
                sx={{ mt: 1 }} 
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                New This Month
              </Typography>
              <Typography variant="h4" color="info.main">
                {analytics.newCustomersThisMonth}
              </Typography>
              <LinearProgress variant="determinate" value={75} sx={{ mt: 1 }} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Avg Order Value
              </Typography>
              <Typography variant="h4" color="primary.main">
                ${analytics.averageOrderValue.toFixed(0)}
              </Typography>
              <LinearProgress variant="determinate" value={60} sx={{ mt: 1 }} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs
          value={selectedTab}
          onChange={(_, newValue) => setSelectedTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="All Customers" />
          <Tab label="Top Customers" />
          <Tab label="Segments" />
          <Tab label="Analytics" />
        </Tabs>
      </Paper>

      {selectedTab === 0 && (
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filter.status}
                    onChange={(e) => setFilter(prev => ({ ...prev, status: e.target.value }))}
                    label="Status"
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Source</InputLabel>
                  <Select
                    value={filter.source}
                    onChange={(e) => setFilter(prev => ({ ...prev, source: e.target.value }))}
                    label="Source"
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="direct">Direct</MenuItem>
                    <MenuItem value="referral">Referral</MenuItem>
                    <MenuItem value="website">Website</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={filter.priority}
                    onChange={(e) => setFilter(prev => ({ ...prev, priority: e.target.value }))}
                    label="Priority"
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  startIcon={<FilterList />}
                  onClick={() => setFilter({ status: 'all', source: 'all', priority: 'all', assignedTo: 'all' })}
                >
                  Clear
                </Button>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleOpenDialog}
              disabled={!canManage('manager:customers') && !canManage('admin:customers')}
            >
              Add Customer
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Refresh">
                <IconButton onClick={() => refetch()}>
                  <Refresh />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export">
                <IconButton>
                  <Download />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Total Orders</TableCell>
                  <TableCell>Total Spent</TableCell>
                  <TableCell>Last Order</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedCustomers.map((customer) => (
                  <TableRow
                    key={customer.id}
                    hover
                    onClick={() => handleSelectCustomer(customer)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {customer.id.slice(0, 8)}…
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          <Person />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2">{customer.name}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {customer.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Business fontSize="small" />
                        {customer.company || 'N/A'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{customer.phone}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {customer.address}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{renderStatusChip(customer.status || 'active')}</TableCell>
                    <TableCell>{renderPriorityChip(customer.priority || 'medium')}</TableCell>
                    <TableCell>
                      <Badge badgeContent={customer.totalOrders} color="primary">
                        <ShoppingCart />
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="primary.main" fontWeight="bold">
                        ${(customer.totalSpent || 0).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {customer.lastOrderDate ? format(parseISO(customer.lastOrderDate), 'MMM dd, yyyy') : 'Never'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCustomer(customer);
                              setOpenDialog(true);
                            }}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCustomer(customer);
                              setOpenDialog(true);
                            }}
                            disabled={!canManage('manager:customers') && !canManage('admin:customers')}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCustomer(customer.id);
                            }}
                            disabled={!canManage('manager:customers') && !canManage('admin:customers')}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {selectedTab === 1 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Top Customers by Revenue
          </Typography>
          <Grid container spacing={2}>
            {topCustomers?.slice(0, 6).map((customer, index) => (
              <Grid item xs={12} md={6} lg={4} key={customer.id}>
                <Card>
                  <CardHeader
                    avatar={
                      <Avatar sx={{ bgcolor: index < 3 ? 'primary.main' : 'grey.500' }}>
                        {index + 1}
                      </Avatar>
                    }
                    title={customer.name}
                    subheader={customer.company}
                    action={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Star color="warning" />
                        <Typography variant="h6">${(customer.totalSpent || 0).toLocaleString()}</Typography>
                      </Box>
                    }
                  />
                  <CardContent>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      {customer.totalOrders} orders
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Last order: {customer.lastOrderDate ? format(parseISO(customer.lastOrderDate), 'MMM dd, yyyy') : 'Never'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {selectedTab === 2 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Customer Segments
          </Typography>
          <Grid container spacing={2}>
            {segments.map((segment) => (
              <Grid item xs={12} md={6} lg={4} key={segment.id}>
                <Card>
                  <CardHeader
                    title={segment.name}
                    subheader={segment.criteria}
                  />
                  <CardContent>
                    <Typography variant="h4" color="primary.main">
                      {segment.customerCount}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Average Value: ${segment.averageValue.toLocaleString()}
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={(segment.customerCount / analytics.totalCustomers) * 100} 
                      sx={{ mt: 1 }} 
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {selectedTab === 3 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Customer Analytics
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Customer Satisfaction" />
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h3" color="primary.main">
                      {analytics.customerSatisfactionScore}
                    </Typography>
                    <Typography variant="h6" color="textSecondary">
                      / 5.0
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star key={star} color={star <= analytics.customerSatisfactionScore ? 'warning' : 'disabled'} />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Top Spending Customer" />
                <CardContent>
                  {analytics.topSpendingCustomer ? (
                    <Box>
                      <Typography variant="h6">{analytics.topSpendingCustomer.name}</Typography>
                      <Typography variant="h4" color="primary.main">
                        ${(analytics.topSpendingCustomer?.totalSpent || 0).toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {analytics.topSpendingCustomer.totalOrders} orders
                      </Typography>
                    </Box>
                  ) : (
                    <Typography>No data available</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedCustomer ? 'Edit Customer' : 'Add New Customer'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name *"
                value={selectedCustomer?.name || newCustomer.name}
                onChange={(e) => {
                  if (selectedCustomer) {
                    setSelectedCustomer({ ...selectedCustomer, name: e.target.value });
                  } else {
                    setNewCustomer({ ...newCustomer, name: e.target.value });
                  }
                }}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email *"
                type="email"
                value={selectedCustomer?.email || newCustomer.email}
                onChange={(e) => {
                  if (selectedCustomer) {
                    setSelectedCustomer({ ...selectedCustomer, email: e.target.value });
                  } else {
                    setNewCustomer({ ...newCustomer, email: e.target.value });
                  }
                }}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone *"
                value={selectedCustomer?.phone || newCustomer.phone}
                onChange={(e) => {
                  if (selectedCustomer) {
                    setSelectedCustomer({ ...selectedCustomer, phone: e.target.value });
                  } else {
                    setNewCustomer({ ...newCustomer, phone: e.target.value });
                  }
                }}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Company"
                value={selectedCustomer?.company || newCustomer.company}
                onChange={(e) => {
                  if (selectedCustomer) {
                    setSelectedCustomer({ ...selectedCustomer, company: e.target.value });
                  } else {
                    setNewCustomer({ ...newCustomer, company: e.target.value });
                  }
                }}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={2}
                value={selectedCustomer?.address || newCustomer.address}
                onChange={(e) => {
                  if (selectedCustomer) {
                    setSelectedCustomer({ ...selectedCustomer, address: e.target.value });
                  } else {
                    setNewCustomer({ ...newCustomer, address: e.target.value });
                  }
                }}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Status</InputLabel>
                <Select
                  value={selectedCustomer?.status || newCustomer.status}
                  onChange={(e) => {
                    if (selectedCustomer) {
                      setSelectedCustomer({ ...selectedCustomer, status: e.target.value as 'active' | 'inactive' | 'pending' });
                    } else {
                                              setNewCustomer({ ...newCustomer, status: e.target.value as 'active' | 'inactive' | 'pending' });
                    }
                  }}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Priority</InputLabel>
                <Select
                  value={selectedCustomer?.priority || newCustomer.priority}
                  onChange={(e) => {
                    if (selectedCustomer) {
                      setSelectedCustomer({ ...selectedCustomer, priority: e.target.value as 'high' | 'low' | 'medium' });
                    } else {
                                              setNewCustomer({ ...newCustomer, priority: e.target.value as 'high' | 'low' | 'medium' });
                    }
                  }}
                  label="Priority"
                >
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={selectedCustomer?.notes || newCustomer.notes}
                onChange={(e) => {
                  if (selectedCustomer) {
                    setSelectedCustomer({ ...selectedCustomer, notes: e.target.value });
                  } else {
                    setNewCustomer({ ...newCustomer, notes: e.target.value });
                  }
                }}
                margin="normal"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={selectedCustomer ? handleUpdateCustomer : handleCreateCustomer}
            variant="contained"
            disabled={loading || (!canManage('manager:customers') && !canManage('admin:customers'))}
          >
            {loading ? <CircularProgress size={20} /> : (selectedCustomer ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
